import React, { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

/**
 * Standardized Card Components
 *
 * Follows consistent component API with:
 * - Compound Components: Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter
 * - Variants: default, elevated, outlined, filled, interactive
 * - Sizes: sm, md, lg, xl
 * - States: default, hover, focus, disabled
 * - Accessibility: WCAG 2.1 AAA compliant
 * - Cultural adaptation support
 */

const cardVariants = cva(
  // Base styles - using design system tokens
  "card-base rounded-lg border bg-card text-card-foreground transition-all duration-200",
  {
    variants: {
      variant: {
        default: "border-border bg-card shadow-sm",
        elevated: "border-border bg-card shadow-md hover:shadow-xl hover:-translate-y-1 transition-transform duration-200",
        outlined: "border-2 border-border bg-card shadow-none",
        filled: "border-0 bg-muted shadow-sm",
        interactive: "border-border bg-card shadow-sm hover:shadow-lg hover:border-primary hover:-translate-y-1 cursor-pointer transition-all duration-200",
        glass: "glass-card border-border backdrop-blur-xl",
        gradient: "border-border bg-card shadow-sm",
        featured: "glass-card border-primary/40 dark:border-primary/30 shadow-sm relative overflow-hidden hover:border-primary/60 transition-colors duration-200",
      },
      size: {
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
        xl: "p-10",
      },
      state: {
        default: "",
        hover: "",
        focus: "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        disabled: "opacity-50 cursor-not-allowed",
      },
      fullWidth: {
        true: "w-full",
        false: "w-auto",
      },
    },
    compoundVariants: [
      {
        variant: "elevated",
        size: "sm",
        className: "p-4 shadow-md",
      },
      {
        variant: "interactive",
        state: "focus",
        className: "focus:ring-2 focus:ring-ring focus:ring-offset-2",
      },
      {
        variant: "filled",
        size: "lg",
        className: "p-8 bg-muted",
      },
      {
        variant: "featured",
        state: "focus",
        className: "focus:ring-4 focus:ring-primary/50 focus:ring-offset-2",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      state: "default",
      fullWidth: true,
    },
  }
);

/**
 * Main Card Component
 */

// Gradient fills were decorative gradients; per the flat-surface rule they
// are tonal tints of the same hue (or a flat fill for the -strong keys).
const gradientClasses = {
  'primary': 'bg-primary/[0.04]',
  'success': 'bg-success/[0.04]',
  'info': 'bg-info/[0.04]',
  'subtle': 'bg-muted/50',
  'primary-strong': 'bg-primary text-primary-foreground',
  'success-strong': 'bg-success text-primary-foreground',
};

const Card = forwardRef(({
  className,
  variant = "default",
  size = "md",
  state = "default",
  fullWidth = true,
  gradient = null,
  onClick,
  asChild = false,
  children,
  ...props
}, ref) => {
  // 2026-09-06, Phase 3b: the useDesignSystem/getToken inline styles are
  // gone from Card. They hardcoded light-mode rgb(neutral-*) values OVER
  // the token-driven classes — inline style wins the cascade, so border,
  // title and description colors were locked to light mode and the dark
  // theme never reached them. The classes were always right.

  const cardClasses = cn(
    cardVariants({ variant, size, state, fullWidth }),
    onClick && "cursor-pointer",
    variant === "gradient" && gradient && gradientClasses[gradient],
    className
  );

  const Component = asChild ? React.Fragment : "div";

  return (
    <Component
      ref={ref}
      className={cardClasses}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      {...props}
    >
      {children}
    </Component>
  );
});
Card.displayName = "Card";

/**
 * Card Header Component
 */
/**
 * CardHeader
 *
 * Accepts EITHER the `title`/`description` props or ordinary children
 * (<CardTitle>/<CardDescription>), which is the shadcn/ui convention.
 *
 * `children` was previously not destructured and never rendered, so the
 * conventional form silently produced an empty header:
 *
 *     <CardHeader><CardTitle>Import Bank Statement</CardTitle></CardHeader>
 *
 * 59 files in src/ use exactly that shape and none use the title prop, so every
 * card header in the application was rendering without its heading.
 *
 * The wrapper also carried role="heading" aria-level="2" with no accessible
 * name of its own. That was announced as an empty heading, and once children
 * render it would nest a real <h3> (CardTitle) inside a heading role. CardTitle
 * is the actual heading element, so the role is removed from the container.
 */
const CardHeader = forwardRef(({
  className,
  title,
  description,
  actions,
  decorative = false,
  children,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col space-y-1.5 p-6 pb-4",
        actions && "flex-row items-center justify-between space-y-0",
        className
      )}
      {...props}
    >
      <div className="flex-1 space-y-1">
        {title && <CardTitle>{title}</CardTitle>}
        {description && <CardDescription>{description}</CardDescription>}
        {children}
      </div>

      {actions && (
        <div className="flex items-center space-x-2 ml-4">
          {actions}
        </div>
      )}
    </div>
  );
});
CardHeader.displayName = "CardHeader";

/**
 * Card Title Component
 */
const CardTitle = forwardRef(({
  className,
  level = 3,
  decorative = false,
  children,
  ...props
}, ref) => {
  const HeadingComponent = `h${level}`;

  return (
    <HeadingComponent
      ref={ref}
      className={cn(
        "font-semibold leading-none tracking-tight text-foreground",
        level === 2 && "text-2xl",
        level === 3 && "text-xl",
        level === 4 && "text-lg",
        className
      )}
      {...(!decorative && children && typeof children === 'string' && {
        id: children.replace(/\s+/g, '-').toLowerCase()
      })}
      {...props}
    >
      {children}
    </HeadingComponent>
  );
});
CardTitle.displayName = "CardTitle";

/**
 * Card Description Component
 */
const CardDescription = forwardRef(({
  className,
  children,
  ...props
}, ref) => {
  return (
    <p
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    >
      {children}
    </p>
  );
});
CardDescription.displayName = "CardDescription";

/**
 * Card Content Component
 */
const CardContent = forwardRef(({
  className,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn("p-6 pt-0", className)}
      {...props}
    />
  );
});
CardContent.displayName = "CardContent";

/**
 * Card Footer Component
 */
const CardFooter = forwardRef(({
  className,
  align = "left",
  spacing = "sm",
  children,
  ...props
}, ref) => {
  const alignmentClasses = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
    between: "justify-between",
    around: "justify-around",
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 p-6 pt-0",
        alignmentClasses[align],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
CardFooter.displayName = "CardFooter";

/**
 * Card Media Component (for images, videos, etc.)
 */
const CardMedia = forwardRef(({
  className,
  aspectRatio = "16/9",
  fit = "cover",
  src,
  alt,
  ...props
}, ref) => {
  const aspectRatioClasses = {
    "1/1": "aspect-square",
    "4/3": "aspect-video",
    "16/9": "aspect-video",
    "21/9": "aspect-video",
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-t-lg",
        aspectRatioClasses[aspectRatio],
        className
      )}
    >
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={cn(
          "w-full h-full object-cover",
          fit === "contain" && "object-contain",
          fit === "cover" && "object-cover",
          fit === "fill" && "object-fill"
        )}
        {...props}
      />
    </div>
  );
});
CardMedia.displayName = "CardMedia";

/**
 * Card Stats Component (for displaying metrics)
 */
const CardStats = forwardRef(({
  className,
  stats = [],
  layout = "horizontal",
  ...props
}, ref) => {
  const layoutClasses = {
    horizontal: "flex-row space-x-6",
    vertical: "flex-col space-y-2",
    grid: "grid grid-cols-2 gap-4",
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex",
        layoutClasses[layout],
        className
      )}
      {...props}
    >
      {stats.map((stat, index) => (
        <div key={index} className="space-y-1">
          <div className="text-2xl font-semibold text-foreground">
            {stat.value}
          </div>
          <div className="text-xs text-muted-foreground">
            {stat.label}
          </div>
          {stat.change && (
            <div
              className={cn(
                "text-xs font-medium",
                stat.change.type === 'increase' && "text-success",
                stat.change.type === 'decrease' && "text-destructive"
              )}
            >
              {stat.change.value}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});
CardStats.displayName = "CardStats";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  CardMedia,
  CardStats
};
